<script setup lang="ts">
import { ref } from "vue";

// for merge ce shadow-root styles
defineOptions({
  customOptions: {},
  inheritAttrs: true
});

const props = withDefaults(
  defineProps<{
    plate: string; // 无牌车
    plateColor?:
      | "蓝牌"
      | "b"
      | "绿牌"
      | "g"
      | "黄牌"
      | "y"
      | "黄绿"
      | "q"
      | "黑牌"
      | "d"
      | "白牌"
      | "w"
      | "未知"
      | "unknown";
    display?:
      | "leading-chinese"
      | "leading-dot"
      | "leading-plaintext"
      | "bg-mockcar"
      | "bg-simple";
    fontWeight?: "bold" | "normal";
    plainTextGap?: string;
  }>(),
  {
    display: "leading-chinese",
    fontWeight: "normal",
    plainTextGap: "#e6e6e6"
  }
);

const emits = defineEmits<{
  // (event: "void"): void;
}>();

const PLATE_COLORS: {
  name: string;
  leadingStyle: Record<string, string>;
  bgMockCarStyle: Record<string, string>;
  bgSimpleStyle: Record<string, string>;
}[] = [
  {
    name: "蓝牌",
    leadingStyle: {
      background: "#0078f0",
      color: "#ffffff"
    },
    bgMockCarStyle: {
      background: "#0078f0",
      color: "#ffffff"
    },
    bgSimpleStyle: {
      background: "#0078f0",
      color: "#ffffff"
    }
  },
  {
    // 绿牌一定是8位数
    name: "绿牌",
    leadingStyle: {
      background: "#00f08c",
      color: "#000000"
    },
    bgMockCarStyle: {
      background: "linear-gradient(180deg, #F0FFFA 0%, #00F08C 100%)",
      color: "#000000"
    },
    bgSimpleStyle: {
      background: "#00f08c",
      color: "#000000"
    }
  },
  {
    name: "黄牌",
    leadingStyle: {
      background: "#fac800",
      color: "#000000"
    },
    bgMockCarStyle: {
      background: "#fac800",
      color: "#000000"
    },
    bgSimpleStyle: {
      background: "#fac800",
      color: "#000000"
    }
  },
  {
    // 黄绿车牌一定是8位数
    name: "黄绿",
    leadingStyle: {
      background: "linear-gradient(90deg, #fac800 51.5%, #00f08c 51.5%)",
      color: "#000000"
    },
    bgMockCarStyle: {
      background: `linear-gradient(90deg, #fac800 37%, #00f08c 37%)`,
      color: "#000000"
    },
    bgSimpleStyle: {
      background: `linear-gradient(90deg, #fac800 36%, #00f08c 36%)`,
      color: "#000000"
    }
  },
  {
    name: "黑牌",
    leadingStyle: {
      background: "#222222",
      color: "#ffffff"
    },
    bgMockCarStyle: {
      background: "#222222",
      color: "#ffffff"
    },
    bgSimpleStyle: {
      background: "#222222",
      color: "#ffffff"
    }
  },
  {
    name: "白牌",
    leadingStyle: {
      background: "#ffffff",
      color: "#000000",
      border: "1px solid #999999"
    },
    bgMockCarStyle: {
      background: "#ffffff",
      color: "#000000"
    },
    bgSimpleStyle: {
      background: "#ffffff",
      color: "#000000",
      border: "1px solid #999999"
    }
  },
  {
    name: "未知",
    leadingStyle: {
      background: "#cccccc",
      color: "#ffffff"
    },
    bgMockCarStyle: {
      background: "#cccccc",
      color: "#ffffff"
    },
    bgSimpleStyle: {
      background: "rgba(255,255,255,0)"
      // color: "default-parent-font-color"
    }
  }
];

function getPlateColor(plate: string, color?: string) {
  let name = "未知";
  switch (color) {
    case "b":
    case "蓝牌":
      name = "蓝牌";
      break;
    case "g":
    case "绿牌":
      name = "绿牌";
      break;
    case "y":
    case "黄牌":
      name = "黄牌";
      break;
    case "q":
    case "黄绿":
      name = "黄绿";
      break;
    case "d":
    case "黑牌":
      name = "黑牌";
      break;
    case "w":
    case "白牌":
      name = "白牌";
      break;
  }
  if (plate == "无牌车") {
    name = "未知";
  }
  const result = PLATE_COLORS.filter((item) => item.name === name);
  return result[0];
}

const plateColor = ref(getPlateColor(props.plate, props.plateColor || ""));

defineExpose({});
</script>

<template>
  <div
    class="plate-display"
    :class="display"
  >
    <!-- leading-chinese -->
    <div
      v-if="
        display == 'leading-chinese' &&
        plateColor.name !== '未知' &&
        plate !== '无牌车'
      "
      class="leading"
      :style="plateColor.leadingStyle"
    >
      {{ plateColor.name }}
    </div>
    <!-- leading-dot -->
    <div
      v-if="display == 'leading-dot' && plate !== '无牌车'"
      class="leading"
      :style="{
        ...plateColor.leadingStyle,
        width: '1em',
        height: '1em'
      }"
    ></div>
    <!-- leading-plaintext -->
    <div
      v-if="
        display == 'leading-plaintext' &&
        plateColor.name !== '未知' &&
        plate !== '无牌车'
      "
      class="leading"
      :style="{
        borderRight: `1px solid ${props.plainTextGap}`
      }"
    >
      {{ plateColor.name }}
    </div>
    <!-- bg-mockcar -->
    <div
      v-if="display == 'bg-mockcar'"
      class="text-wrap"
      :style="plateColor.bgMockCarStyle"
    >
      <div
        class="text"
        :style="{
          border: `1px solid ${plateColor.bgMockCarStyle.color}`,
          fontWeight: props.fontWeight
        }"
      >
        {{ plate }}
      </div>
    </div>
    <!-- bg-simple -->
    <div
      v-else-if="display == 'bg-simple'"
      class="text-wrap"
      :style="plateColor.bgSimpleStyle"
    >
      <div
        class="text"
        :style="{
          fontWeight: props.fontWeight
        }"
      >
        {{ plate }}
      </div>
    </div>
    <!-- text -->
    <div
      v-else
      class="text"
      :style="{ fontWeight: props.fontWeight }"
    >
      {{ plate }}
    </div>
  </div>
</template>

<style scoped>
.plate-display {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  line-height: 1;
  font-size: 1em; /* default 1em = 16px */
}
.plate-display .leading {
  box-sizing: border-box;
  font-size: 0.875em; /* default 0.875em = 14px */
  border-radius: 2px;
  margin-right: 5px;
}
.plate-display.leading-chinese .leading {
  padding: 2px 4px;
}
.plate-display.leading-dot .leading {
  border-radius: 50%;
}
.plate-display.leading-plaintext .leading {
  padding-right: 5px;
  border-radius: 0;
}
.plate-display .text {
  line-height: 1;
  box-sizing: border-box;
}
.plate-display.bg-mockcar .text-wrap {
  border-radius: 6px;
}
.plate-display.bg-simple .text-wrap {
  border-radius: 2px;
}
.plate-display.bg-mockcar .text-wrap .text {
  font-size: 1.5em; /* default 1.5em = 24px */
  margin: 2px;
  padding: 9px 10px;
  border-radius: 4px;
}
.plate-display.bg-simple .text-wrap .text {
  font-size: 0.875em; /* default 0.875em = 14px */
  padding: 2px 4px;
}
</style>
